/* PreNumbers PSBB — Products (reskinned).

   Logic (add product, the per-product editor) is unchanged — only the markup is
   reskinned to the .pn-* design system, mirroring Financial Builder. The editor
   itself is FB's shared ProductDetails (already modernized); this page just
   provides the card grid + modal chrome and the investor-ready flag. */

import { useState, useContext } from 'react';
import { Modal } from 'antd';

import { PSSBPageContext } from '../PSBBPage.jsx';
import { industryDetails } from '../../constants.jsx';
import { genericPOSTDatabaseCall } from '../../helperFunctions.jsx';
import { ProductDetails } from '../../FinancialBuilder/ProductsPage/ProductsPage.jsx';
import { PageHead, FbIcons } from '../../FinancialBuilder/fbUI.jsx';

const { ArrowRightOutlined, ArrowLeftOutlined, PlusOutlined } = FbIcons;

async function addProductToDatabase(product) {
  return genericPOSTDatabaseCall('/api/fb/addProduct', { product }, 'product');
}

function pct(v) { const n = Number(v); return Number.isFinite(n) ? (n * 100).toFixed(n < 0.1 ? 1 : 0) + '%' : '—'; }
function MiniSpark({ color }) {
  const pts = [12, 19, 28, 41, 58].map((v, i) => `${(i / 4) * 220},${40 - ((v - 12) / 46) * 34 - 3}`);
  return <svg viewBox="0 0 220 40" width="100%" height="40" preserveAspectRatio="none"><polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function ProductCard({ product, onClick }) {
  const sub = product.revenue_model === 'Subscription Model';
  return (
    <div className="pn-card" onClick={onClick}>
      <div className="pn-card-top">
        <div><div className="pn-card-name">{product.name}</div><div className="pn-card-desc">{product.description || 'No description'}</div></div>
        <span className={"pn-badge " + (sub ? "sub" : "sales")}>{sub ? 'Subscription' : 'Sales'}</span>
      </div>
      <div className="pn-card-spark"><MiniSpark color={sub ? 'var(--pn-brand-teal)' : 'var(--pn-brand-yellow)'} /></div>
      <div className="pn-card-metrics">
        <div className="pn-metric"><div className="ml">Annual price</div><div className="mv accent">${Number(product.annual_price || 0).toLocaleString()}</div></div>
        <div className="pn-metric"><div className="ml">Growth</div><div className="mv">{pct(product.growth_rate)}</div></div>
        <div className="pn-metric"><div className="ml">Churn</div><div className="mv">{pct(product.churn_rate)}</div></div>
        <div className="pn-metric"><div className="ml">Capture</div><div className="mv">{pct(product.market_capture)}</div></div>
      </div>
    </div>
  );
}

function ProductsPage({ showNavigationControls = true }) {
  const { goTo, companySelected, setCompanySelected, messageApi, useInvestorReadyFinancial } = useContext(PSSBPageContext);
  const [newProductName, setNewProductName] = useState("");
  const [editingId, setEditingId] = useState(null);

  if (!companySelected) return null;
  const editing = companySelected.products?.find((p) => p.id === editingId) || null;

  const addNewProduct = async () => {
    if (!newProductName) { messageApi.error('Please enter a product name'); return; }
    messageApi.loading('Adding product...', 0);
    try {
      const newProduct = await addProductToDatabase({
        name: newProductName, company_id: companySelected.fb_id,
        growth_rate: industryDetails[companySelected.industry]['default_growth_rate'],
        growth_adjustment: industryDetails[companySelected.industry]['default_growth_adjustment'],
      });
      setCompanySelected((prev) => ({
        ...prev, products: [...prev.products, {
          ...newProduct,
          market_size: industryDetails[companySelected.industry]['market_size'],
          churn_rate: industryDetails[companySelected.industry]['churn_rate']
        }]
      }));
      setNewProductName('');
      messageApi.destroy(); messageApi.success('Product added successfully');
    } catch (e) { console.error('Error adding product:', e); messageApi.destroy(); messageApi.error('Failed to add product'); }
  };

  return (
    <div className="pn-wrap">
      <PageHead eyebrow={companySelected.name} title="Products"
        sub={<><b>{companySelected.products?.length || 0}</b> products feeding the revenue model.</>}>
        {showNavigationControls && <>
          <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(1)}><ArrowLeftOutlined /> Back</button>
          <button type="button" className="pn-btn pn-btn-cta" disabled={(companySelected.products?.length || 0) < 1} onClick={() => goTo(3)}>
            {(companySelected.products?.length || 0) < 1 && <>Need at least 1 Product</>}
            {(companySelected.products?.length || 0) > 0 && <>View Dashboard <ArrowRightOutlined /></>}
          </button>
        </>}
      </PageHead>

      <div className="pn-grid">
        {(companySelected.products || []).map((p) => <ProductCard key={p.id} product={p} onClick={() => setEditingId(p.id)} />)}
        <div className="pn-add-tile" onClick={() => document.getElementById('pn-new-product')?.focus()}>
          <div className="ring"><PlusOutlined /></div><span>Add product</span>
        </div>
      </div>

      <div className="pn-addrow">
        <div className="pn-input-wrap" style={{ flex: 1 }}>
          <input id="pn-new-product" className="pn-control" placeholder="New product name" value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNewProduct()} />
        </div>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addNewProduct}><PlusOutlined /> Add product</button>
      </div>

      <Modal title={editing ? `Edit ${editing.name}` : ''} open={!!editing} footer={null}
        onCancel={() => setEditingId(null)} width={"70%"} style={{ top: 20 }}>
        {editing && (
          <div className="pn-modal-embed">
            <ProductDetails product={editing} setCompanySelected={setCompanySelected} messageApi={messageApi}
              companySelected={companySelected} modalView useInvestorReadyFinancial={useInvestorReadyFinancial} onDone={() => setEditingId(null)} />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ProductsPage;
